import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, LoadingSpinner, DatePicker, Button, CheckboxDropdown } from 'yankees-react-utils';
import styled from 'styled-components';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useOktaQuery, Stats, Team } from '../../../../data';
import usePlayerSelector from '../../../../utils/usePlayerSelector';
import PlayerSelector from '../../../../components/PlayerSelector';

const DatePickerWrapper = styled.div`
    max-width: 120px;
`;

const StyledCheckboxDropdown = styled(CheckboxDropdown)`
    .dropdown-toggle {
        width: 90px;
    }
    .btn:disabled {
        background-color: #e9ecef !important;
        opacity: 1;
    }
`;

const InputField = ({ label, children }) => (
    <div className="d-flex align-items-center me-4">
        <span className="me-1">{ label }</span>
        {children}
    </div>
);

const getLevelOptions = (pitchingSeasons) => {
    const extra = [{ Abbr: 'All', LevelID: null, SortOrder: 0 }, { Abbr: 'MIN', LevelID: 7, SortOrder: 2 }];

    const levels = pitchingSeasons.map((bs) => bs.Level);

    return extra.concat([...new Set(levels.map((l) => l.LevelID))].map((levelId) => levels.find((l) => l.LevelID === levelId)))
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((level) => ({
            name: level.Abbr,
            value: level.LevelID,
        }));
};

const getOrgOptions = (orgs) => {
    const all = [{ name: 'All', value: null }];

    return all.concat(orgs.map((o) => ({
        name: o.Abbr,
        value: o.OrganizationID,
    })));
};

const getYearOptions = (pitchingSeasons) => [...new Set(pitchingSeasons?.map((bs) => bs.YearID)) ?? []].sort((a, b) => b - a).map((year) => ({
    name: `All ${year}`,
    value: year,
}));

const TopLevelFilter = ({ defaultLevelId, levels, setLevels, org, setOrg, year, setYear, startDate, setStartDate, endDate, setEndDate, pitcher, setPitcher }) => {
    const { t: translate } = useTranslation();
    const player = usePlayerSelector();

    const [innerPitcher, setInnerPitcher] = useState(pitcher);
    const [innerLevels, setInnerLevels] = useState(levels);
    const [innerOrg, setInnerOrg] = useState(org);
    const [innerYear, setInnerYear] = useState(year);
    const [innerStartDate, setInnerStartDate] = useState(startDate);
    const [innerEndDate, setInnerEndDate] = useState(endDate);

    const pitcherId = player.IsCatcher ? innerPitcher?.PlayerID : player.PlayerID;

    const { data: allOrgs, isLoading: isLoadingOrgs } = useOktaQuery(['allOrgs'], (t) => Team.bylevel(t, 8));
    const { data: pitchingSeasons, isLoading: isLoadingPitchingSeasons } = useOktaQuery(['pitchingSeasons', pitcherId],
        (t) => Stats.seasonPitching(t, { playerId: pitcherId }),
        { enabled: !!pitcherId });

    useEffect(() => {
        if (pitchingSeasons && pitchingSeasons.length && !levels.filter((x) => x.checked).length) {
            if (player.IsCatcher) {
                setInnerYear(getYearOptions(pitchingSeasons)[0]);
            } else {
                setYear(getYearOptions(pitchingSeasons)[0]);
            }
        }
    }, [pitchingSeasons, setYear, levels, player.IsCatcher]);

    useEffect(() => {
        if (pitchingSeasons) {
            const toSet = getLevelOptions(pitchingSeasons).map((lo) => ({
                ...lo,
                checked: lo.value === defaultLevelId ?? null,
            }));
            if (toSet.filter((x) => x.checked).length === 0) {
                toSet[0].checked = true;
            }
            if (player.IsCatcher) {
                setInnerLevels(toSet);
            } else {
                setLevels(toSet);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultLevelId, pitchingSeasons]);

    const applyFilters = () => {
        setPitcher(innerPitcher);
        setLevels(innerLevels);
        setOrg(innerOrg);
        setYear(innerYear);
        setStartDate(innerStartDate);
        setEndDate(innerEndDate);
    };

    useEffect(() => { setInnerPitcher(pitcher); }, [pitcher]);
    useEffect(() => { setInnerLevels(levels); }, [levels]);
    useEffect(() => { setInnerOrg(org); }, [org]);
    useEffect(() => { setInnerYear(year); }, [year]);
    useEffect(() => { setInnerStartDate(startDate); }, [startDate]);
    useEffect(() => { setInnerEndDate(endDate); }, [endDate]);

    const onLevelDropdownChange = (o, key, e) => {
        if (key === 'All' && e.target.checked) {
            setInnerLevels(innerLevels.map((l, i) => ({
                ...l,
                checked: !i,
            })));
        } else {
            const newLevels = innerLevels.map((lo) => ({
                ...lo,
                checked: lo.name === o.name ? e.target.checked : !!lo.checked,
            }));
            if (newLevels[0].checked && newLevels.filter((l) => !!l.checked).length > 1) {
                newLevels[0].checked = false;
            } else if (!newLevels.filter((l) => !!l.checked).length) {
                newLevels[0].checked = true;
            }

            setInnerLevels(newLevels);
        }
    };
    const handleYearSelect = (newYear) => {
        setInnerStartDate(new Date(newYear != null ? newYear.value : moment().year(), 0, 1));
        setInnerEndDate(new Date(newYear != null ? newYear.value + 1 : moment().year(), 1, 15));
        setInnerYear(newYear);
    };

    const selectedLevels = innerLevels.filter((l) => !!l.checked);
    const levelPlaceholder = selectedLevels.length === 1 ? selectedLevels[0].name : `${selectedLevels.length} Levels`;

    return (
        <div className="border rounded py-2 px-3 d-flex align-items-center bg-white">
            {player.IsCatcher && (
                <>
                    <div className="me-1">{translate('baseball.pitcher_one')}:</div>
                    <PlayerSelector
                        teamid={player.Team.TeamID}
                        position="pitcher"
                        player={innerPitcher}
                        onSelect={setInnerPitcher}
                        title={translate('common.select-a-pitcher')}
                        className="me-3"
                    />
                </>
            )}
            {levels && (
                isLoadingPitchingSeasons ? <LoadingSpinner /> : (
                    <InputField label="Level:">
                        <StyledCheckboxDropdown
                            value={innerLevels}
                            onChange={onLevelDropdownChange}
                            options={innerLevels}
                            titleOverrideFormat
                            title={levelPlaceholder}
                            style={{ width: 90 }}
                            disabled={!pitcherId}
                        />
                    </InputField>
                )
            )}
            <InputField label="Opp:">
                {isLoadingOrgs ? <LoadingSpinner /> : <Dropdown value={innerOrg} onSelect={setInnerOrg} options={getOrgOptions(allOrgs)} />}
            </InputField>
            <InputField label="Date:">
                <Dropdown value={innerYear} onSelect={handleYearSelect} options={getYearOptions(pitchingSeasons)} />
            </InputField>
            <InputField label="Start:">
                <DatePickerWrapper><DatePicker value={innerStartDate} onSelect={setInnerStartDate} maxDate={innerEndDate} /></DatePickerWrapper>
                <span className="mx-2">to</span>
                <DatePickerWrapper><DatePicker value={innerEndDate} onSelect={setInnerEndDate} minDate={innerStartDate} alignRight /></DatePickerWrapper>
            </InputField>
            <Button variant="primary" onClick={applyFilters}>Apply</Button>
        </div>
    );
};

TopLevelFilter.propTypes = {
    playerId: PropTypes.number.isRequired,
    defaultLevelId: PropTypes.number,
    levels: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
    })).isRequired,
    setLevels: PropTypes.func.isRequired,
    org: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
    }).isRequired,
    setOrg: PropTypes.func.isRequired,
    year: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
    }),
    setYear: PropTypes.func.isRequired,
    startDate: PropTypes.instanceOf(Date),
    setStartDate: PropTypes.func.isRequired,
    endDate: PropTypes.instanceOf(Date),
    setEndDate: PropTypes.func.isRequired,
    pitcher: PropTypes.shape({
        PlayerID: PropTypes.number,
    }),
    setPitcher: PropTypes.func.isRequired,
};

TopLevelFilter.defaultProps = {
    defaultLevelId: null,
    year: null,
    startDate: null,
    endDate: null,
    pitcher: null,
};

export default TopLevelFilter;
